import { IBetaEstimateResult } from '@models/beta/beta-estimate';
import { IBetaTimeSeries } from '@models/beta/betaTimeSeries';
import { IMarketRate } from '@models/beta/marketrate';
import { filterTimeSeriesByMonthYear, getDateString, getLastDayOfNextMonth } from '@shared/dates';
import * as Highcharts from 'highcharts';
import { IExportChartOption } from '../export.config';

const BALANCE_GRAPH_ID = 'BALANCE_GRAPH';
const COUPON_GRAPH_ID = 'COUPON_GRAPH';
const MARKET_RATE_GRAPH_ID = 'MARKET_RATE_GRAPH';
const PREDICTED_COUPON_GRAPH_ID = 'PREDICTED_COUPON_GRAPH';

const COLOUR_BRIGHT_GREEN = '#59B565';
const LIGHT_BLUE = '#D9E1F2';
const COLOUR_DARK_NAVY = '#2B495E';
const COLOUR_ORANGE = '#FF7410';

export const betaChartJSON = (
  startDate: Date,
  endDate: Date,
  betaTimeSeries: IBetaTimeSeries,
  marketRate: IMarketRate,
  estimate: IBetaEstimateResult,
  exportChartOption: IExportChartOption
) => {
  // Disable use of UTC
  Highcharts.setOptions({
    time: { useUTC: false },
    lang: { numericSymbols: ['K', ' M', 'B', 'T'] }
  });

  const chartObj = {
    exporting: {
      sourceWidth: exportChartOption.width,
      sourceHeight: exportChartOption.height,
      allowHTML: true
    },
    chart: {
      id: 'timeSeriesGraph',
      boost: {
        useGPUTranslations: true,
        allowForce: true
      },
      zoomType: 'x'
    },
    title: { text: '' },
    xAxis: {
      type: 'datetime'
    },
    yAxis: [
      {
        title: {
          text: 'USD$'
        }
      },

      {
        title: {
          text: 'Percentage %'
        },
        opposite: true
      }
    ],
    legend: {
      layout: 'vertical',
      align: 'right',
      verticalAlign: 'middle',
      alignColumns: true,
      itemWidth: 160
    },

    plotOptions: {
      area: {
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 1
          },
          stops: [
            [0, Highcharts.getOptions().colors[0]],
            [1, LIGHT_BLUE]
          ]
        },
        marker: {
          enabled: false
        },
        lineWidth: 1,
        states: {
          hover: {
            lineWidth: 1
          }
        },
        threshold: null
      }
    },

    series: [
      {
        type: 'spline',
        dashStyle: 'LongDash',
        name: 'Predicted Coupon (%)',
        showInLegend: false,
        id: PREDICTED_COUPON_GRAPH_ID,
        yAxis: 1,
        color: COLOUR_ORANGE,
        marker: {
          enabled: false
        },
        data: []
      }
    ],
    credits: {
      enabled: false
    },
    navigation: {
      buttonOptions: {
        verticalAlign: 'top',
        y: 22
      }
    }
  };

  // Trimming all series appropriately

  const [balanceStart, balanceEnd] = filterTimeSeriesByMonthYear(startDate, endDate, betaTimeSeries.PeriodEndDateSeries);
  betaTimeSeries.ProductBalanceSeries = betaTimeSeries.ProductBalanceSeries.slice(balanceStart, balanceEnd + 1);
  betaTimeSeries.PeriodEndDateSeries = betaTimeSeries.PeriodEndDateSeries.slice(balanceStart, balanceEnd + 1);
  betaTimeSeries.ProductRateSeries = betaTimeSeries.ProductRateSeries.slice(balanceStart, balanceEnd + 1);

  if (estimate.Lag) {
    marketRate.TimeSeries.map(val => {
      val.PeriodEndDate = getDateString(getLastDayOfNextMonth(new Date(val.PeriodEndDate), +estimate.Lag));
    });
  }

  // tslint:disable-next-line:prefer-const
  let [marketRateStart, marketRateEnd] = filterTimeSeriesByMonthYear(
    startDate,
    endDate,
    marketRate.TimeSeries.map(mr => mr.PeriodEndDate)
  );

  // ! Fix 8267
  if (marketRateEnd === -1) {
    marketRateEnd = marketRate.TimeSeries.length - 1;
  }

  marketRate.TimeSeries = marketRate.TimeSeries.slice(marketRateStart, marketRateEnd + 1);

  // Adding balance
  const balanceData = [];

  betaTimeSeries.ProductBalanceSeries.forEach((bal, index) => {
    const time = new Date(betaTimeSeries.PeriodEndDateSeries[index]).getTime();
    const obj = [time, bal];
    balanceData.push(obj);
  });

  const totalBalanceSeries = {
    type: 'area',
    name: 'Balance ($)',
    data: balanceData,
    id: BALANCE_GRAPH_ID,
    marker: {
      enabled: false
    }
  };

  // @ts-ignore
  chartObj.series.push(totalBalanceSeries);

  // Adding coupon data (product rate)
  const couponData = [];
  betaTimeSeries.ProductRateSeries.forEach((coupon, index) => {
    const time = new Date(betaTimeSeries.PeriodEndDateSeries[index]).getTime();
    const arr = [time, coupon * 100];
    couponData.push(arr);
  });

  const couponSeries = {
    type: 'spline',
    name: 'Coupon (%)',
    data: couponData,
    id: COUPON_GRAPH_ID,
    yAxis: 1,
    color: COLOUR_BRIGHT_GREEN,
    marker: {
      enabled: false
    }
  };

  // @ts-ignore
  chartObj.series.push(couponSeries);

  // Now adding market rate data
  const marketRateData = [];
  marketRate.TimeSeries.forEach(mr => {
    const date = new Date(mr.PeriodEndDate).getTime();
    marketRateData.push([date, mr.Rate * 100]);
  });

  const marketRateSeries = {
    type: 'spline',
    name: `${marketRate.MarketRateName}`,
    data: marketRateData,
    id: MARKET_RATE_GRAPH_ID,
    yAxis: 1,
    color: COLOUR_DARK_NAVY,
    marker: {
      enabled: false
    }
  };

  // @ts-ignore
  chartObj.series.push(marketRateSeries);

  return chartObj;
};
